<template>
  <div class="modal-parent">
    <div
      id="credential-management-modal"
      v-shortkey="['esc']"
      class="modal"
      @shortkey="closeModal"
    >
      <confirmation-modal
        v-if="showDeleteConfirmation"
        :confirmation-type="confirmation.type"
        :title="confirmation.title"
        :description="confirmation.description"
        :disclaimer="confirmation.disclaimer"
        :button-text="confirmation.button"
        @confirm="deleteCredentials"
        @cancel="toggleDeleteConfirmation"
      />
      <div class="modal_topbar modal_topbar--no-actions">
        <div
          id="title-credential-management-modal"
          class="modal_topbar-title modal_topbar-title--no-actions"
          style="display: inline-flex"
        >
          <span> Delete Credentials: {{ user.firstName + " " + user.lastName }} </span>
          <user-icon :roles="user.roles" />
        </div>
        <div class="modal_topbar_action modal_topbar_action--no-actions">
          <template v-if="fetching || deleting">
            <loader :size="25" />
          </template>
        </div>
        <span
          id="exit-credential-modal"
          class="modal_topbar-exit"
          @click="closeModal"
          v-html="closeIcon"
        />
      </div>
      <div class="credential-item credential-item__header">
        <span>#</span>
        <span>Added On</span>
        <span>Last Transaction</span>
        <span>Source</span>
        <span />
      </div>
      <div class="credential-container">
        <template v-if="credentialsList.length > 0">
          <div class="credential-table">
            <template v-for="(credential, index) in formattedCredentialRow">
              <label
                class="credential-item"
                :id="credential.uuid"
                :key="credential.uuid"
                :for="credential.uuid"
                :data-testid="'credential-item-' + index"
              >
                <span class="credential-item-cell">
                  {{ index + 1 }}
                </span>
                <span
                  class="credential-item-cell"
                  :data-testid="'credential-row-addedOn-' + index"
                >
                  {{ credential.addedOn }}
                </span>
                <span
                  class="credential-item-cell"
                  :data-testid="'credential-row-lastTransaction-' + index"
                >
                  {{ credential.lastTransaction }}
                </span>
                <span
                  class="credential-item-cell"
                  :data-testid="'credential-row-source-' + index"
                >
                  {{ credential.source }}
                </span>
                <span class="credential-item-cell credential-item-cell--right">
                  <!--Don't show button but still keep col aligned -->
                  <button
                    v-show="credential.source !== 'ACM'"
                    v-tooltip="{ content: 'Delete Credential' }"
                    class="btn__icon"
                    @click="confirmDeleteCredentialType(credentialType.Credential, credential.uuid, index)"
                    v-html="deleteIcon"
                    :data-testid="'credential-row-delete-' + index"
                  />
                </span>
              </label>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="empty-state">
            <template v-if="fetching">
              Fetching credentials...
            </template>
            <template v-else>
              <p>User has no credentials</p>
            </template>
          </div>
        </template>
      </div>

      <div class="modal_footer">
        <transition name="pop">
          <div
            v-show="credentialsList.length > 0"
            class="dropdown"
          >
            <button
              id="manage-credentials-dropdown-button"
              class="dropdown dropdown-button dropdown-button--red"
            >
              Delete All
              <span
                class="dropdown-button-chevron"
                v-html="chevronDown"
              />
            </button>
            <div
              id="manage-credentials-dropdown-content"
              class="dropdown-content"
            >
              <template v-if="user.hasFingerData">
                <div
                  id="delete-finger-data-dropdown"
                  class="dropdown-item"
                  @click="confirmDeleteCredentialType(credentialType.BiometricTemplates)"
                >
                  Biometric Templates
                </div>
                <div class="dropdown__divider" />
              </template>
              <template v-if="user.hasPin">
                <div
                  id="delete-pin-data-dropdown"
                  class="dropdown-item"
                  @click="confirmDeleteCredentialType(credentialType.PINs)"
                >
                  PINS
                </div>
                <div class="dropdown__divider" />
              </template>
              <div
                id="delete-all-credentials-dropdown"
                class="dropdown-item"
                @click="confirmDeleteCredentialType(credentialType.All)"
              >
                <b>All Credentials</b>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <div class="modal-overlay" />
  </div>
</template>

<script>
import cardQueries from '@/graphql/cards'
import userQueries from '@/graphql/users'
import Loader from './loader'
import feather from 'feather-icons'
import UserIcon from './userIcon'
import ConfirmationModal from '@/components/confirmationModal.vue'

export default {
  name: 'CredentialManagementModal',
  components: {
    loader: Loader,
    UserIcon,
    ConfirmationModal,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  mixins: [
    cardQueries,
    userQueries
  ],
  computed: {
    formattedCredentialRow() {
      const _credentials = this.credentialsList
      const options = {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      }
      const _formattedcredentials = _credentials.map((credential) => {
        const {acmId, createdAt, lastUsedAt, uuid} = credential
        return {
          addedOn: createdAt ? new Date(createdAt).toLocaleString('en-US', options) : '',
          lastTransaction: lastUsedAt
            ? new Date(lastUsedAt).toLocaleString('en-US', options)
            : 'N/A',
          source: acmId === 'undefined - Bypass' ? 'Link' : 'ACM', // Default acmId for non ACM creds is set to 'undefined - Bypass'
          uuid: uuid,
        }
      })
      return _formattedcredentials
    },
    isCurrentUser() {
      const _currentUser = this.getUsername()
      return this.user ? this.user.username === _currentUser : false
    }
  },
  created() {
    this.fetchCredentials()
  },
  data() {
    return {
      fetching: false,
      deleting: false,
      credentialsList: [],
      showDeleteConfirmation: false,
      deleteCard: {
        uuid: '',
        index: -1,
      },
      errorMessage: '',
      closeIcon: feather.icons.x.toSvg(),
      deleteIcon: feather.icons['trash-2'].toSvg(),
      infoIcon: feather.icons.info.toSvg({width: '1em', height: '1em'}),
      adminIcon: feather.icons.shield.toSvg({width: '1em', height: '1em'}),
      groupAdminIcon: feather.icons.users.toSvg({width: '1em', height: '1em'}),
      chevronDown: feather.icons['chevron-down'].toSvg(),
      disclaimer: 'Deleting a Card or UserID will also remove any fingerprints or PINs linked to it',
      confirmation: {
        type: 'danger',
        title: '',
        description: '',
        disclaimer: '',
        button: '',
      },
      credentialType: {
        PINs: 'PINs',
        BiometricTemplates: 'Biometric Templates',
        Credential: 'Card/UserID',
        All: 'Credentials',
      },
      deleteCredentialType: '',
    }
  },
  methods: {
    async fetchCredentials() {
      this.fetching = true
      try {
        const _result = await this.listCardsQuery({
          username: this.user.username,
        })
        this.credentialsList = _result.sort((a, b) => {
          // There is a weird behaviour seen on Firefox vs Chrome and Edge.
          // Firefox sorts this in descending order whereas the other browser sort it in ascending order.
          // To maintain consistency and avoid browser specific JS engines, we convert these values to a common
          // format and then compare them
          const dateA = new Date(a.createdAt).getTime()
          const dateB = new Date(b.createdAt).getTime()
          return dateA - dateB
        })
        this.fetching = false
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to retrieve credentials.')
      }
    },
    async deleteCredential(uuid, index) {
      await this.deleteCardMutation({ uuid: uuid })
      this.credentialsList.splice(index, 1)
    },
    async deleteAllCredentials() {
      /*
       * Create a deep copy of the cards list to avoid accessing the object that is being
       * updated during the delete card call
       */
      const cardListCopy = this.credentialsList.map((cred) => {
        return cred
      })

      for (const card of cardListCopy) {
        // Sending index = 0 to remove the first element in the credentialsList since it is being updated as we delete
        await this.deleteCredential(card.uuid, 0)
      }
    },
    async deleteCredentials() {
      this.showDeleteConfirmation = false
      this.deleting = true
      let successAlertMessage = `Deleted all ${this.deleteCredentialType}`
      let failureAlertMessage = `Unable to delete all ${this.deleteCredentialType}`
      try {
        switch (this.deleteCredentialType) {
        case this.credentialType.PINs:
          await this.deleteUserPins(this.user.username)
          break
        case this.credentialType.BiometricTemplates:
          await this.deleteUserFingerTemplates(this.user.username)
          break
        case this.credentialType.Credential:
          await this.deleteCredential(this.deleteCard.uuid, this.deleteCard.index)
          successAlertMessage = 'Deleted Credential'
          failureAlertMessage = 'Unable to delete Credential'
          break
        case this.credentialType.All:
          await this.deleteAllCredentials()
          break
        default:
          throw new Error(`Invalid credential type: ${this.deleteCredentialType}`)
        }
        this.deleting = false
        this.$emit('update-prop')
        this.displayAlert('success', successAlertMessage)
      } catch (error) {
        this.deleting = false
        this.displayAlert('fail', failureAlertMessage)
      }
    },
    toggleDeleteConfirmation() {
      this.showDeleteConfirmation = !this.showDeleteConfirmation
    },
    confirmDeleteCredentialType(credentialType, uuid, index) {
      this.deleteCredentialType = credentialType
      this.deleteCard.uuid = uuid
      this.deleteCard.index = index

      // Update confirmation modal to correspond to the correct credential type
      this.confirmation.title = credentialType == this.credentialType.Credential ? 'Delete Credential?' : `Delete All ${credentialType}?`
      this.confirmation.description = credentialType == this.credentialType.Credential ? 'Are you sure you want to delete this credential?' : `Are you sure you want to delete all ${credentialType} for this user? This cannot be undone.`
      this.confirmation.button = credentialType == this.credentialType.Credential ? 'Delete' : 'Delete All'
      this.confirmation.disclaimer = ''

      if (credentialType == this.credentialType.All || credentialType == this.credentialType.Credential) {
        this.confirmation.disclaimer = this.disclaimer
      }

      this.showDeleteConfirmation = true
    },
    closeModal() {
      this.$emit('close')
    },
  },
}
</script>
<style lang="scss" scoped>
@import "@/scss/_tables.scss";

.credential {
  &-container {
    width: 100%;
    min-height: 150px;
    max-height: 500px;
    overflow-y: auto;
  }
  &-table {
    border-collapse: collapse;
    width: 100%;
    max-height: 300px;
    &-cell {
      font-weight: normal;
      text-align: left;
      padding: 8px;
      border-bottom: 1px solid $light-grey;
      &--right {
        text-align: right;
      }
    }
    @media #{$mobile} {
      overflow-x: auto;
    }
  }
  &-item {
    @include grid-table-item();
    grid-template-columns: 0.3fr 4fr 4fr 2fr 2fr;
    &__header {
      @include grid-table-header();
    }
    &-cell {
      font-weight: normal;
      text-align: left;
    }
    @media #{$mobile} {
      width: 1000px;
    }
  }
  &-actionbar {
    @include flex-display();
    @include flex-align-items(center);
    @include flex-justify-content(space-between);
    font-size: $small-font;
    &-container {
      position: relative;
      @include flex-display();
      @include flex-align-items(center);
    }
    &__selection {
      color: $black;
      font-style: italic;
    }
  }
  &-delete {
    position: absolute;
    width: 100%;
    &-menu {
      align-items: end;
      margin: 10;
    }
  }
}

.dropdown-content {
  position: absolute;
  top: 0;
  transform: translate(0px, -100%);
  left: 100%;
  z-index: 1;
}

.left-justify {
  margin-left: auto;
}

</style>
